import { createCaseAttributeSchema, type CaseAttributeDto, type CaseAttributeKind } from '@tms/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Button, Dialog, Field, Input } from '../../components/ui';
import { useErrorText, useT } from '../../i18n';
import { useCreateCaseAttribute, useUpdateCaseAttribute } from './api';
import { CASE_ATTRIBUTE_MESSAGES } from './messages';

interface CaseAttributeValues {
  name: string;
}

export interface CaseAttributeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectCode: string;
  kind: CaseAttributeKind;
  /** Set when editing an existing layer or module. */
  attribute?: CaseAttributeDto | undefined;
}

export const CaseAttributeFormDialog = ({
  open,
  onOpenChange,
  projectCode,
  kind,
  attribute,
}: CaseAttributeFormDialogProps) => {
  const { t } = useT();
  const { errorText, applyApiError } = useErrorText();
  const text = CASE_ATTRIBUTE_MESSAGES[kind];
  const isEdit = attribute !== undefined;
  const create = useCreateCaseAttribute(projectCode, kind);
  const update = useUpdateCaseAttribute(projectCode, kind);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<CaseAttributeValues>({
    resolver: zodResolver(createCaseAttributeSchema),
    defaultValues: { name: '' },
  });

  useEffect(() => {
    if (!open) return;
    reset({ name: attribute?.name ?? '' });
  }, [open, attribute, reset]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (isEdit) {
        await update.mutateAsync({ attributeId: attribute.id, input: values });
        toast.success(t(text.updated, { name: values.name }));
      } else {
        await create.mutateAsync(values);
        toast.success(t(text.created, { name: values.name }));
      }
      onOpenChange(false);
    } catch (error) {
      if (!applyApiError(error, setError, ['name'])) {
        toast.error(errorText(error, text.saveFailed));
      }
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? t(text.editTitle) : t(text.new)}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={() => void onSubmit()} loading={isSubmitting}>
            {isEdit ? t('common.saveChanges') : t(text.create)}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        <Field label={t('common.name')} error={errors.name?.message} required>
          {({ id, invalid, describedBy }) => (
            <Input
              id={id}
              invalid={invalid}
              aria-describedby={describedBy}
              autoFocus
              placeholder={t(text.newPlaceholder)}
              {...register('name')}
            />
          )}
        </Field>

        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
};
